import { Button } from "@/components/ui/button"
import { Download, Eye, Trash } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { DocumentDeleteDialog } from "./DocumentDeleteDialog"
import { useToast } from "@/hooks/use-toast"
import { Checkbox } from "@/components/ui/checkbox"
import { useDocxExport } from "@/hooks/useDocxExport"

interface DocumentData {
  id: string
  title: string
  content: string
  type: string
  created_at: string
}

interface DocumentTableProps {
  documents: DocumentData[]
  onView: (document: DocumentData) => void
  onDelete: (id: string) => void
  onDeleteSelected: (ids: string[]) => void
  selectedDocuments: string[]
  onSelectDocument: (id: string, checked: boolean) => void
  onSelectAll: (checked: boolean) => void
}

export const DocumentTable = ({ 
  documents, 
  onView, 
  onDelete, 
  onDeleteSelected,
  selectedDocuments,
  onSelectDocument,
  onSelectAll
}: DocumentTableProps) => {
  const { toast } = useToast()
  const { exportToDocx } = useDocxExport()

  const handleExportDOCX = async (content: string, title: string) => {
    try {
      await exportToDocx(content, title);
      
      toast({
        title: "DOCX exportado com sucesso!",
        description: "O documento foi salvo em formato DOCX.",
      })
    } catch (error) {
      console.error('Erro ao exportar DOCX:', error)
      toast({
        title: "Erro ao exportar DOCX",
        description: "Ocorreu um erro ao gerar o documento. Por favor, tente novamente.",
        variant: "destructive",
      })
    }
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const allSelected = documents.length > 0 && selectedDocuments.length === documents.length

  return (
    <div className="space-y-4">
      {selectedDocuments.length > 0 && (
        <div className="bg-primary/5 p-4 rounded-lg flex items-center justify-between">
          <span className="text-sm text-primary">
            {selectedDocuments.length} documento{selectedDocuments.length > 1 ? 's' : ''} selecionado{selectedDocuments.length > 1 ? 's' : ''}
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDeleteSelected(selectedDocuments)}
            className="hover:bg-destructive/90"
          >
            <Trash className="w-4 h-4 mr-2" />
            Excluir Selecionados
          </Button>
        </div>
      )}
      
      <div className="bg-white/30 backdrop-blur-sm rounded-xl overflow-hidden border border-primary/10 shadow-lg">
        <div className="overflow-x-auto w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox 
                    checked={allSelected}
                    onCheckedChange={(checked) => onSelectAll(checked as boolean)}
                  />
                </TableHead>
                <TableHead className="text-left">Horário</TableHead>
                <TableHead className="text-left">Tipo</TableHead>
                <TableHead className="text-left">Data de Criação</TableHead>
                <TableHead className="text-right w-[100px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Checkbox 
                      checked={selectedDocuments.includes(doc.id)}
                      onCheckedChange={(checked) => onSelectDocument(doc.id, checked as boolean)}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-left">{formatTime(doc.created_at)}</TableCell>
                  <TableCell className="text-left">{doc.type}</TableCell>
                  <TableCell className="text-left">
                    {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onView(doc)}
                        className="hover:bg-primary/10 hover:text-primary"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleExportDOCX(doc.content, doc.title)}
                        className="hover:bg-primary/10 hover:text-primary"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <DocumentDeleteDialog
                        id={doc.id}
                        onConfirm={() => onDelete(doc.id)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
